/**
 * Speech-engine choices for voice. UI labels map to allowlisted gateway names only
 * (never model/device/executable paths).
 */

export const STT_PROVIDER = Object.freeze({
  MOONSHINE: 'moonshine',
  PIPECAT: 'pipecat',
});

/** Canonical picker options. Hidden when the gateway's `available` list excludes them. */
export const STT_OPTION_DEFS = Object.freeze([
  { value: STT_PROVIDER.MOONSHINE, label: 'Moonshine' },
  { value: STT_PROVIDER.PIPECAT, label: 'Pipecat' },
]);

const KNOWN = new Set(['moonshine', 'pipecat', 'whisper', 'openai', 'deepgram']);

const FALLBACK = Object.freeze({
  default: 'whisper',
  available: ['whisper', 'moonshine'],
  aliases: { pipecat: 'whisper' },
});

/** Pull STT readiness from either the flat `stt_providers` shape or nested ready payloads. */
const readinessSource = (raw) => {
  if (!raw || typeof raw !== 'object') return FALLBACK;
  if (Array.isArray(raw.available) || raw.default || raw.aliases) return raw;
  const nested =
    raw.stt_providers ||
    raw.speech?.stt ||
    raw.providers?.stt ||
    null;
  return nested && typeof nested === 'object' ? nested : FALLBACK;
};

export const normalizeSttProviders = (raw) => {
  const src = readinessSource(raw);
  const available = [];
  for (const item of Array.isArray(src.available) ? src.available : FALLBACK.available) {
    const key = String(item || '').toLowerCase();
    if (KNOWN.has(key) && !available.includes(key)) available.push(key);
  }
  if (!available.length) available.push(...FALLBACK.available);

  const aliases = {};
  const rawAliases = src.aliases && typeof src.aliases === 'object' ? src.aliases : FALLBACK.aliases;
  for (const [srcKey, dst] of Object.entries(rawAliases)) {
    if (typeof srcKey !== 'string' || typeof dst !== 'string') continue;
    const s = srcKey.toLowerCase();
    const d = dst.toLowerCase();
    if (KNOWN.has(s) && KNOWN.has(d)) aliases[s] = d;
  }
  if (!aliases.pipecat && available.includes('whisper')) aliases.pipecat = 'whisper';

  let def = typeof src.default === 'string' ? src.default.toLowerCase() : '';
  if (typeof src.provider === 'string' && !def) def = src.provider.toLowerCase();
  if (!available.includes(def) && def !== 'whisper' && def !== 'pipecat') {
    def = available[0] || FALLBACK.default;
  }
  return { default: def || FALLBACK.default, available, aliases };
};

/**
 * Product picker always offers Moonshine and Pipecat. Gateway `available` only
 * drives the default; an unsupported choice fails cleanly at session create.
 */
export const isSttOptionAvailable = () => true;

export const sttOptionsForProviders = () => [...STT_OPTION_DEFS];

/** Prefer the gateway default; map whisper → Pipecat for the UI value. */
export const preferredSttProvider = (providers, current) => {
  const options = sttOptionsForProviders(providers);
  if (!options.length) return STT_PROVIDER.PIPECAT;
  if (current && options.some((o) => o.value === current)) return current;
  const info = normalizeSttProviders(providers);
  if (info.default === 'moonshine' && options.some((o) => o.value === STT_PROVIDER.MOONSHINE)) {
    return STT_PROVIDER.MOONSHINE;
  }
  if (options.some((o) => o.value === STT_PROVIDER.PIPECAT)) return STT_PROVIDER.PIPECAT;
  return options[0].value;
};

export const sttProviderLabel = (value) =>
  STT_OPTION_DEFS.find((o) => o.value === value)?.label || value || 'speech engine';
